import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { MessageProps } from './features/screens/dialogs/components/Message';
import { LocalMessage, serverChatToLocal, serverMessageToLocal } from './features/types/chat';
import { ChatSyncService, ChatApiService } from './features/utils/chatApiUtils';

// Утилита для сортировки чатов от новых к старым
const sortChatsByLastMessageTime = (chats: Chat[]): Chat[] => {
  return [...chats].sort((a, b) => {
    const timeA = new Date(a.lastMessageTime).getTime();
    const timeB = new Date(b.lastMessageTime).getTime();
    return timeB - timeA; // Обратный порядок для сортировки от новых к старым
  });
};

export interface Chat {
  id: string;
  title: string;
  lastMessageTime: string;
  messages: MessageProps[];
  createdAt: string;
  serverId?: number;
  isDirty?: boolean;
}

export interface ChatState {
  chats: Chat[];
  selectedChatId: string | null;
  isLoading: boolean;
  isSyncing: boolean;
  lastSyncTime: string | null;
  error: string | null;
  isOnline: boolean;
  totalChats: number;
}

const initialState: ChatState = (() => {
  const savedChats = localStorage.getItem('chats');
  const savedSelectedChat = localStorage.getItem('selectedChatId');
  const savedLastSyncTime = localStorage.getItem('lastSyncTime');

  let chats: Chat[] = [];
  if (savedChats) {
    chats = JSON.parse(savedChats);
  } else {
    // Инициализируем пустым массивом, если чатов нет
    chats = [];
    localStorage.setItem('chats', JSON.stringify(chats));
  }

  return {
    chats,
    selectedChatId: savedSelectedChat || (chats.length > 0 ? chats[0].id : null),
    isLoading: false,
    isSyncing: false,
    lastSyncTime: savedLastSyncTime || null,
    error: null,
    isOnline: navigator.onLine,
    totalChats: chats.length,
  };
})();

// Асинхронные thunk'и для работы с сервером
export const syncChatsFromServer = createAsyncThunk(
  'chat/syncChatsFromServer',
  async (_, { rejectWithValue, getState }) => {
    try {
      const localChats = await ChatSyncService.syncAllChats();
      console.log(`✅ Синхронизация завершена: получено ${localChats.length} чатов`);
      return localChats;
    } catch (error) {
      console.error('❌ Ошибка синхронизации чатов:', error);
      return rejectWithValue(error instanceof Error ? error.message : 'Ошибка синхронизации');
    }
  }
);

// Пагинированная загрузка чатов (метаданные, без сообщений)
export const loadChatsPage = createAsyncThunk(
  'chat/loadChatsPage',
  async ({ skip, limit }: { skip: number; limit: number }, { rejectWithValue }) => {
    try {
      const page = await ChatApiService.getChatsPage(skip, limit);
      const localChats: Chat[] = page.items.map((serverChat) =>
        serverChatToLocal(serverChat, []) as unknown as Chat
      );

      return {
        chats: localChats,
        total: page.total,
        skip: page.skip,
        limit: page.limit,
        hasNext: page.has_next,
      };
    } catch (error) {
      console.error('❌ Ошибка пагинированной загрузки чатов:', error);
      return rejectWithValue(error instanceof Error ? error.message : 'Ошибка загрузки чатов');
    }
  }
);

// Ленивое получение истории сообщений выбранного чата
export const loadChatMessages = createAsyncThunk(
  'chat/loadChatMessages',
  async (
    { chatId, serverChatId }: { chatId: string; serverChatId: number },
    { rejectWithValue }
  ) => {
    try {
      const chatWithMessages = await ChatApiService.getChatWithMessages(serverChatId);
      const localMessages = chatWithMessages.messages.map(serverMessageToLocal);
      return {
        chatId,
        messages: localMessages as unknown as MessageProps[],
      };
    } catch (error) {
      console.error(`❌ Ошибка загрузки сообщений для чата ${serverChatId}:`, error);
      return rejectWithValue(
        error instanceof Error ? error.message : 'Ошибка загрузки сообщений чата'
      );
    }
  }
);

export const createChatOnServer = createAsyncThunk(
  'chat/createChatOnServer',
  async ({ title, firstMessage }: { title: string; firstMessage?: MessageProps }, { rejectWithValue }) => {
    try {
      const localFirstMessage: LocalMessage | undefined = firstMessage ? {
        sender: firstMessage.sender,
        time: firstMessage.time,
        type: firstMessage.type,
        text: firstMessage.text,
        title: firstMessage.title,
        transcription: firstMessage.transcription,
        specialty: firstMessage.specialty,
        speakerId: firstMessage.speakerId,
        json: firstMessage.json,
        isDirty: true
      } : undefined;

      const localChat = await ChatSyncService.createChatOnServer(title, localFirstMessage);
      return localChat;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Ошибка создания чата');
    }
  }
);

export const syncMessageToServer = createAsyncThunk(
  'chat/syncMessageToServer',
  async ({ chatId, message }: { chatId: string; message: MessageProps }, { rejectWithValue }) => {
    try {
      const localMessage: LocalMessage = {
        sender: message.sender,
        time: message.time,
        type: message.type,
        text: message.text,
        title: message.title,
        transcription: message.transcription,
        specialty: message.specialty,
        speakerId: message.speakerId,
        json: message.json,
        isDirty: true
      };

      const serverChatId = parseInt(chatId);
      const syncedMessage = await ChatSyncService.syncMessage(localMessage, serverChatId);
      return { chatId, syncedMessage };
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Ошибка синхронизации сообщения');
    }
  }
);

export const deleteChatOnServer = createAsyncThunk(
  'chat/deleteChatOnServer',
  async (chatId: string, { rejectWithValue }) => {
    try {
      const serverChatId = parseInt(chatId);
      await ChatSyncService.deleteChatOnServer(serverChatId);
      return chatId;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Ошибка удаления чата');
    }
  }
);

export const updateChatOnServer = createAsyncThunk(
  'chat/updateChatOnServer',
  async ({ chatId, updateData }: { chatId: string; updateData: { title?: string; description?: string; is_active?: boolean } }, { rejectWithValue }) => {
    try {
      const serverChatId = parseInt(chatId);
      const updatedServerChat = await ChatSyncService.updateChatOnServer(serverChatId, updateData);
      return { chatId, updateData, serverChat: updatedServerChat };
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Ошибка обновления чата');
    }
  }
);

const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    addChat: (state, action: PayloadAction<Chat>) => {
      // Простой синхронный добавление чата (для локального использования)
      const newChat = action.payload;
      state.chats.unshift(newChat);
      state.selectedChatId = newChat.id;
      state.totalChats += 1;
      localStorage.setItem('chats', JSON.stringify(state.chats));
      localStorage.setItem('selectedChatId', state.selectedChatId);
    },

    removeMessagesByTitle: (state, action: PayloadAction<{ chatId: string; title: string }>) => {
      const { chatId, title } = action.payload;
      const chatIndex = state.chats.findIndex(chat => chat.id === chatId);
      if (chatIndex !== -1) {
        state.chats[chatIndex].messages = state.chats[chatIndex].messages.filter(msg => msg.title !== title);
        localStorage.setItem('chats', JSON.stringify(state.chats));
      }
    },

    removePlaceholderMessages: (state, action: PayloadAction<{ chatId: string }>) => {
      const { chatId } = action.payload;
      const chatIndex = state.chats.findIndex(chat => chat.id === chatId);
      if (chatIndex !== -1) {
        state.chats[chatIndex].messages = state.chats[chatIndex].messages.filter(msg => msg.text !== 'Ожидаем распознавание…' && msg.text !== 'Ожидаем распознавание...');
        localStorage.setItem('chats', JSON.stringify(state.chats));
      }
    },

    setOnlineStatus: (state, action: PayloadAction<boolean>) => {
      state.isOnline = action.payload;
    },

    clearError: (state) => {
      state.error = null;
    },

    markChatAsDirty: (state, action: PayloadAction<string>) => {
      const chatIndex = state.chats.findIndex(chat => chat.id === action.payload);
      if (chatIndex !== -1) {
        state.chats[chatIndex].isDirty = true;
        localStorage.setItem('chats', JSON.stringify(state.chats));
      }
    },

    markMessageAsDirty: (state, action: PayloadAction<{ chatId: string; messageIndex: number }>) => {
      const { chatId, messageIndex } = action.payload;
      const chatIndex = state.chats.findIndex(chat => chat.id === chatId);
      if (chatIndex !== -1 && state.chats[chatIndex].messages[messageIndex]) {
        // Добавляем флаг isDirty к сообщению (если его нет в MessageProps, используем any)
        (state.chats[chatIndex].messages[messageIndex] as any).isDirty = true;
        state.chats[chatIndex].isDirty = true;
        localStorage.setItem('chats', JSON.stringify(state.chats));
      }
    },

    updateChat: (state, action: PayloadAction<{ id: string; updates: Partial<Chat> }>) => {
      const { id, updates } = action.payload;
      const chatIndex = state.chats.findIndex(chat => chat.id === id);
      if (chatIndex !== -1) {
        state.chats[chatIndex] = { ...state.chats[chatIndex], ...updates };
        localStorage.setItem('chats', JSON.stringify(state.chats));
      }
    },

    updateMessageTranscription: (state, action: PayloadAction<{ chatId: string; messageIndex: number; transcription: string }>) => {
      const { chatId, messageIndex, transcription } = action.payload;
      const chatIndex = state.chats.findIndex(chat => chat.id === chatId);
      if (chatIndex !== -1 && state.chats[chatIndex].messages[messageIndex]) {
        state.chats[chatIndex].messages[messageIndex].transcription = transcription;
        localStorage.setItem('chats', JSON.stringify(state.chats));
      }
    },

    addMessageToChat: (state, action: PayloadAction<{ chatId: string; message: MessageProps }>) => {
      const { chatId, message } = action.payload;
      const chatIndex = state.chats.findIndex(chat => chat.id === chatId);
      if (chatIndex !== -1) {
        // Проверяем, есть ли уже сообщение с таким serverId
        if (message.serverId) {
          const existingMessageIndex = state.chats[chatIndex].messages.findIndex(
            msg => msg.serverId === message.serverId
          );

          if (existingMessageIndex !== -1) {
            // Обновляем существующее сообщение вместо создания дубликата
            console.log(`Обновляем существующее сообщение с ID ${message.serverId}`);
            state.chats[chatIndex].messages[existingMessageIndex] = message;
            state.chats[chatIndex].lastMessageTime = message.time;
            localStorage.setItem('chats', JSON.stringify(state.chats));
            return;
          }
        }

        // Добавляем новое сообщение, если дубликат не найден
        state.chats[chatIndex].messages.push(message);
        state.chats[chatIndex].lastMessageTime = message.time;
        // Обновляем заголовок чата, если это первое сообщение
        if (state.chats[chatIndex].messages.length === 1 && message.text) {
          if (message.type === 'audio') {
            state.chats[chatIndex].title = 'Голосовое сообщение';
          } else {
            state.chats[chatIndex].title = message.text.substring(0, 30) + (message.text.length > 30 ? '...' : '');
          }
        }
        // Сортируем чаты по времени последнего сообщения
        state.chats = sortChatsByLastMessageTime(state.chats);
        localStorage.setItem('chats', JSON.stringify(state.chats));
      }
    },

    setMessagesForChat: (state, action: PayloadAction<{ chatId: string; messages: MessageProps[] }>) => {
      const { chatId, messages } = action.payload;
      const chatIndex = state.chats.findIndex(chat => chat.id === chatId);
      if (chatIndex !== -1) {
        // Удаляем дубликаты по serverId перед установкой сообщений
        const uniqueMessages = messages.filter((message, index, array) => {
          // Если у сообщения есть serverId, проверяем уникальность
          if (message.serverId) {
            return array.findIndex(m => m.serverId === message.serverId) === index;
          }
          // Для сообщений без serverId оставляем как есть
          return true;
        });

        state.chats[chatIndex].messages = uniqueMessages;
        localStorage.setItem('chats', JSON.stringify(state.chats));
      }
    },

    selectChat: (state, action: PayloadAction<string>) => {
      state.selectedChatId = action.payload;
      localStorage.setItem('selectedChatId', state.selectedChatId);
    },

    deleteChat: (state, action: PayloadAction<string>) => {
      const chatId = action.payload;
      const beforeLength = state.chats.length;
      state.chats = state.chats.filter(chat => chat.id !== chatId);
      if (state.chats.length < beforeLength) {
        state.totalChats = Math.max(0, state.totalChats - 1);
      }

      // Если удаляем выбранный чат, выбираем первый доступный или null
      if (state.selectedChatId === chatId) {
        state.selectedChatId = state.chats.length > 0 ? state.chats[0].id : null;
      }

      localStorage.setItem('chats', JSON.stringify(state.chats));
      if (state.selectedChatId !== null) {
        localStorage.setItem('selectedChatId', state.selectedChatId);
      } else {
        localStorage.removeItem('selectedChatId');
      }
    },

    clearAllChats: (state) => {
      state.chats = [];
      state.selectedChatId = null;
      state.totalChats = 0;
      localStorage.removeItem('chats');
      localStorage.removeItem('selectedChatId');
    },


    removeChat: (state, action: PayloadAction<string>) => {
      const chatId = action.payload;
      state.chats = state.chats.filter(chat => chat.id !== chatId);
      if (state.selectedChatId === chatId) {
        state.selectedChatId = state.chats.length > 0 ? state.chats[0].id : null;
      }
      localStorage.setItem('chats', JSON.stringify(state.chats));
      localStorage.setItem('selectedChatId', state.selectedChatId || '');
    },

    // Заменить локальный чат (офлайн) на созданный на сервере с новым ID
    replaceChatId: (state, action: PayloadAction<{ oldId: string; newChat: Chat }>) => {
      const { oldId, newChat } = action.payload;
      const idx = state.chats.findIndex(c => c.id === oldId);
      if (idx !== -1) {
        state.chats[idx] = newChat;
        if (state.selectedChatId === oldId) {
          state.selectedChatId = newChat.id;
        }
        localStorage.setItem('chats', JSON.stringify(state.chats));
        localStorage.setItem('selectedChatId', state.selectedChatId || '');
      }
    },
  },
  extraReducers: (builder) => {
    // Синхронизация чатов с сервера
    builder
      .addCase(syncChatsFromServer.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(syncChatsFromServer.fulfilled, (state, action) => {
        state.isLoading = false;
        // Смерджим серверные чаты с локальными, чтобы не терять несинхрон. сообщения
        const serverChats: Chat[] = action.payload;
        const localChatsMap = new Map(state.chats.map((c) => [c.id, c]));

        const mergedChats: Chat[] = serverChats.map((serverChat) => {
          const localChat = localChatsMap.get(serverChat.id);
          if (!localChat) return serverChat;

          const preservedLocal = (localChat.messages || []).filter(
            (m: any) => !(m as any).serverId // локальные черновики
          );

          // Уникальные серверные + локальные черновики в конце (сохраним порядок)
          const uniqueServerMessages = serverChat.messages.filter((m: any, idx: number, arr: any[]) => {
            if (!(m as any).serverId) return true;
            return arr.findIndex((x: any) => (x as any).serverId === (m as any).serverId) === idx;
          });

          const mergedMessages = [...uniqueServerMessages, ...preservedLocal];

          return {
            ...serverChat,
            messages: mergedMessages,
            // Обновим время последнего сообщения по объединенному списку
            lastMessageTime:
              mergedMessages.length > 0 ? mergedMessages[mergedMessages.length - 1].time : serverChat.lastMessageTime,
          };
        });

        // Добавим локальные чаты, которые отсутствуют на сервере (созданы офлайн)
        const serverIds = new Set(mergedChats.map((c) => c.id));
        for (const localChat of state.chats) {
          if (!serverIds.has(localChat.id)) {
            mergedChats.push(localChat);
          }
        }

        state.chats = sortChatsByLastMessageTime(mergedChats);
        state.lastSyncTime = new Date().toISOString();
        localStorage.setItem('chats', JSON.stringify(state.chats));
        localStorage.setItem('lastSyncTime', state.lastSyncTime);
      })
      .addCase(syncChatsFromServer.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Пагинированная загрузка чатов
    builder
      .addCase(loadChatsPage.pending, (state, action) => {
        // Для первой страницы показываем "Загрузка чатов..."
        if ((action.meta.arg as any).skip === 0) {
          state.isLoading = true;
        } else {
          state.isSyncing = true;
        }
        state.error = null;
      })
      .addCase(loadChatsPage.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSyncing = false;

        const { chats: incomingChats, total, skip } = action.payload;

        if (skip === 0) {
          // Первая страница — заменяем серверные чаты, сохраняя чисто локальные (офлайн)
          const incomingIds = new Set(incomingChats.map(c => c.id));
          const offlineChats = state.chats.filter(c => c.isDirty && !incomingIds.has(c.id));
          const merged = [...incomingChats, ...offlineChats];
          state.chats = sortChatsByLastMessageTime(merged);
        } else {
          // Последующие страницы — добавляем новые, избегаем дубликатов
          const existingIds = new Set(state.chats.map(c => c.id));
          const newOnes = incomingChats.filter(c => !existingIds.has(c.id));
          state.chats = sortChatsByLastMessageTime([...state.chats, ...newOnes]);
        }

        state.totalChats = total;
        state.lastSyncTime = new Date().toISOString();
        localStorage.setItem('chats', JSON.stringify(state.chats));
        localStorage.setItem('lastSyncTime', state.lastSyncTime);
      })
      .addCase(loadChatsPage.rejected, (state, action) => {
        state.isLoading = false;
        state.isSyncing = false;
        state.error = action.payload as string;
      });

    // Ленивое получение истории сообщений выбранного чата
    builder
      .addCase(loadChatMessages.pending, (state) => {
        state.isSyncing = true;
        state.error = null;
      })
      .addCase(loadChatMessages.fulfilled, (state, action) => {
        state.isSyncing = false;
        const { chatId, messages } = action.payload;
        const chatIndex = state.chats.findIndex((chat) => chat.id === chatId);
        if (chatIndex === -1) return;

        // Удаляем дубликаты по serverId, как в setMessagesForChat
        const uniqueMessages = messages.filter((message, index, array) => {
          if (message.serverId) {
            return array.findIndex((m) => m.serverId === message.serverId) === index;
          }
          return true;
        });

        state.chats[chatIndex].messages = uniqueMessages;
        localStorage.setItem('chats', JSON.stringify(state.chats));
      })
      .addCase(loadChatMessages.rejected, (state, action) => {
        state.isSyncing = false;
        state.error = action.payload as string;
      });

    // Создание чата на сервере
    builder
      .addCase(createChatOnServer.pending, (state) => {
        state.isSyncing = true;
        state.error = null;
      })
      .addCase(createChatOnServer.fulfilled, (state, action) => {
        state.isSyncing = false;
        const newChat = {
          ...action.payload,
          // Гарантируем, что новый чат окажется вверху списка
          lastMessageTime: new Date().toISOString(),
        };
        state.chats.unshift(newChat as Chat);
        state.chats = sortChatsByLastMessageTime(state.chats);
        state.selectedChatId = newChat.id;
        state.totalChats += 1;
        localStorage.setItem('chats', JSON.stringify(state.chats));
        localStorage.setItem('selectedChatId', state.selectedChatId);
      })
      .addCase(createChatOnServer.rejected, (state, action) => {
        state.isSyncing = false;
        state.error = action.payload as string;
      });

    // Синхронизация сообщения на сервер
    builder
      .addCase(syncMessageToServer.pending, (state) => {
        state.isSyncing = true;
        state.error = null;
      })
      .addCase(syncMessageToServer.fulfilled, (state, action) => {
        state.isSyncing = false;
        const { chatId, syncedMessage } = action.payload;
        const chatIndex = state.chats.findIndex(chat => chat.id === chatId);
        if (chatIndex !== -1) {
          // Ищем сообщение без serverId (локальное сообщение, которое нужно обновить)
          const messageIndex = state.chats[chatIndex].messages.findIndex(
            (msg: any) => !msg.serverId && msg.isDirty
          );

          if (messageIndex !== -1) {
            // Обновляем найденное сообщение с серверными данными
            (state.chats[chatIndex].messages[messageIndex] as any).serverId = syncedMessage.serverId;
            (state.chats[chatIndex].messages[messageIndex] as any).isDirty = false;
          }
          state.chats[chatIndex].isDirty = false;
          localStorage.setItem('chats', JSON.stringify(state.chats));
        }
      })
      .addCase(syncMessageToServer.rejected, (state, action) => {
        state.isSyncing = false;
        state.error = action.payload as string;
      });

    // Удаление чата на сервере
    builder
      .addCase(deleteChatOnServer.pending, (state) => {
        state.isSyncing = true;
        state.error = null;
      })
      .addCase(deleteChatOnServer.fulfilled, (state, action) => {
        state.isSyncing = false;
        const chatId = action.payload;
        const beforeLength = state.chats.length;
        state.chats = state.chats.filter(chat => chat.id !== chatId);
        if (state.chats.length < beforeLength) {
          state.totalChats = Math.max(0, state.totalChats - 1);
        }

        if (state.selectedChatId === chatId) {
          state.selectedChatId = state.chats.length > 0 ? state.chats[0].id : null;
        }

        localStorage.setItem('chats', JSON.stringify(state.chats));
        if (state.selectedChatId !== null) {
          localStorage.setItem('selectedChatId', state.selectedChatId);
        } else {
          localStorage.removeItem('selectedChatId');
        }
      })
      .addCase(deleteChatOnServer.rejected, (state, action) => {
        state.isSyncing = false;
        state.error = action.payload as string;
      })
      .addCase(updateChatOnServer.pending, (state) => {
        state.isSyncing = true;
        state.error = null;
      })
      .addCase(updateChatOnServer.fulfilled, (state, action) => {
        state.isSyncing = false;
        const { chatId, updateData } = action.payload;
        const chatIndex = state.chats.findIndex(chat => chat.id === chatId);

        if (chatIndex !== -1) {
          // Обновляем локальный чат с новыми данными
          state.chats[chatIndex] = {
            ...state.chats[chatIndex],
            ...updateData,
            lastMessageTime: state.chats[chatIndex].lastMessageTime // Сохраняем время последнего сообщения
          };
          localStorage.setItem('chats', JSON.stringify(state.chats));
        }
      })
      .addCase(updateChatOnServer.rejected, (state, action) => {
        state.isSyncing = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  addChat,
  updateChat,
  updateMessageTranscription,
  addMessageToChat,
  setMessagesForChat,
  selectChat,
  deleteChat,
  removeChat,
  clearAllChats,
  setOnlineStatus,
  clearError,
  markChatAsDirty,
  markMessageAsDirty,
  removeMessagesByTitle,
  removePlaceholderMessages
} = chatSlice.actions;

export default chatSlice.reducer; 